import { CalendarIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  InputGroup,
  InputRightElement,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useBankedMinutesJobsAiCapabilities } from "./bankedMinutes.ai";
import { LocalDate } from "@js-joda/core";
import axios from "axios";
import React from "react";
import { auth } from "../../../../core/auth";
import { env } from "../../../../env";
import DataTable from "../../../../shared/components/DataTable/DataTable";
import useQueryDataTable from "../../../../shared/components/DataTable/useQueryDataTable";
import RangeDatePicker from "../../../../shared/components/DatePicker/RangeDatePicker";
import { createFilters } from "../../../../shared/hooks/useFilters";
import useApi from "../../../../shared/hooks/useApi";
import { usePageFetchingObserver } from "../../../../shared/hooks/usePageIndication";
import { formatErrorResponse } from "../../../../shared/utils/format-response-error";

type BankedMinutesJob = {
  id: number;
  type: "reset" | "update";
  status: "running" | "stopped" | "completed" | "error";
  createdAt: string;
  createdByName: string;
  errorMessage: string | null;
  fileName: string | null;
  totalItems: number;
  completedItems: number;
  errorItems: number;
  cancelledItems: number;
  pendingItems: number;
};

type BankedMinutesJobItem = {
  id: number;
  visitInstanceId: number | null;
  patientDisplayId: number | null;
  patientName: string | null;
  month: string | null;
  bankedMinutes: number | null;
  status: "pending" | "processing" | "completed" | "error" | "cancelled";
  errorMessage: string | null;
  processedAt: string | null;
};

type FilterState = {
  from: string;
  to: string;
};

const initialFilterState: FilterState = {
  from: LocalDate.now().minusMonths(1).toString(),
  to: LocalDate.now().toString(),
};

const { createRangeDatePickerFilter } = createFilters<FilterState>();

function getStatusColor(status: BankedMinutesJob["status"]): string {
  switch (status) {
    case "running":
      return "blue";
    case "stopped":
      return "orange";
    case "completed":
      return "green";
    case "error":
      return "red";
  }
}

export default function BankedMinutesJobsTab() {
  const [filters, setFilters] = React.useState<FilterState>(initialFilterState);

  // This tab tells the omnibar what its own filters are, while it is on screen.
  useBankedMinutesJobsAiCapabilities({
    setFilter: (key, value) => setFilters((prev) => ({ ...prev, [key]: value })),
  });
  const { queries } = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [expandedJobId, setExpandedJobId] = React.useState<number | null>(null);

  const queryParams = {
    from: filters.from,
    to: filters.to,
  };

  const queryOptions = queries.bankedMinutes.jobs(queryParams);

  const fromToFilter = createRangeDatePickerFilter({
    label: "Date range",
    startDate: {
      name: "from",
      value: filters.from ? LocalDate.parse(filters.from) : null,
    },
    endDate: {
      name: "to",
      value: filters.to ? LocalDate.parse(filters.to) : null,
    },
    onChange: (key, value) => setFilters((prev) => ({ ...prev, [key]: value?.toString() })),
  });

  const query = useQuery({
    ...queryOptions,
    select: (data: { list: BankedMinutesJob[] }) => data.list,
    refetchInterval: (query) => {
      const data = query.state.data as { list: BankedMinutesJob[] } | undefined;
      if (data?.list?.some((job) => job.status === "running")) {
        return 5000;
      }
      return false;
    },
  });

  const jobItemsQuery = useQuery({
    ...queries.bankedMinutes.jobItems(expandedJobId ?? 0),
    select: (data: { list: BankedMinutesJobItem[] }) => data.list,
    enabled: expandedJobId !== null,
  });

  const stopMutation = useMutation({
    mutationFn: async (jobId: number) => {
      const authState = auth.getAuthOrFail();
      await axios.post(
        `${env.API_URL}agencies/${authState.data.agency.id}/agency_members/${authState.data.agencyMember.id}/banked_minutes/jobs/${jobId}/stop`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${authState.tokens.authToken}`,
          },
        }
      );
    },
    onSuccess: () => {
      toast({
        title: "Job stop requested",
        status: "success",
        position: "top-right",
      });
      queryClient.invalidateQueries({ queryKey: ["banked_minutes", "jobs"] });
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not stop job",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  const columns = React.useMemo(
    () =>
      createColumns(
        (jobId) => stopMutation.mutate(jobId),
        stopMutation.isPending,
        (jobId) => setExpandedJobId((prev) => (prev === jobId ? null : jobId))
      ),
    [stopMutation]
  );

  const { dataTableProps } = useQueryDataTable({
    query,
    columns,
    columnVisiblity: {},
  });

  usePageFetchingObserver(queryOptions);

  const actionNode = (
    <Button
      colorScheme="blue"
      isDisabled={query.isFetching}
      leftIcon={<SearchIcon />}
      onClick={() => query.refetch()}
    >
      Search
    </Button>
  );

  const filterNode = (
    <InputGroup width="xs">
      <RangeDatePicker {...fromToFilter} inputProps={{ width: "full" }} />
      <InputRightElement>
        <CalendarIcon _groupFocusWithin={{ color: "blue" }} color="gray.400" />
      </InputRightElement>
    </InputGroup>
  );

  return (
    <Box>
      <DataTable
        {...dataTableProps}
        actionNode={actionNode}
        filterNode={filterNode}
        spacing="tight"
      />

      <Drawer
        isOpen={expandedJobId !== null}
        placement="right"
        size="lg"
        onClose={() => setExpandedJobId(null)}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Job Items (Job #{expandedJobId})</DrawerHeader>
          <DrawerBody>
            {jobItemsQuery.isLoading && <Text>Loading items...</Text>}
            {jobItemsQuery.isError && <Text color="red.500">Failed to load items</Text>}
            {jobItemsQuery.data && (
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>ID</Th>
                    <Th>Patient</Th>
                    <Th>Visit</Th>
                    <Th>Month</Th>
                    <Th>Minutes</Th>
                    <Th>Status</Th>
                    <Th>Error</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {jobItemsQuery.data.map((item) => (
                    <Tr key={item.id}>
                      <Td>{item.id}</Td>
                      <Td>
                        {item.patientName != null
                          ? `${item.patientName} (${item.patientDisplayId})`
                          : item.patientDisplayId}
                      </Td>
                      <Td>{item.visitInstanceId}</Td>
                      <Td>{item.month}</Td>
                      <Td>{item.bankedMinutes}</Td>
                      <Td>
                        <Badge
                          colorScheme={
                            item.status === "completed"
                              ? "green"
                              : item.status === "error"
                              ? "red"
                              : item.status === "cancelled"
                              ? "orange"
                              : "gray"
                          }
                        >
                          {item.status}
                        </Badge>
                      </Td>
                      <Td>
                        {item.errorMessage && (
                          <Text color="red.500" fontSize="xs">
                            {item.errorMessage}
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}

const { accessor, display } = createColumnHelper<BankedMinutesJob>();

const createColumns = (
  onStop: (jobId: number) => void,
  isStopping: boolean,
  onViewItems: (jobId: number) => void
) => [
  accessor("id", {
    header: "ID",
    sortingFn: "basic",
  }),
  accessor("type", {
    header: "Type",
    sortingFn: "alphanumeric",
  }),
  display({
    id: "status",
    cell: (x) => (
      <Badge colorScheme={getStatusColor(x.row.original.status)}>{x.row.original.status}</Badge>
    ),
    header: "Status",
  }),
  accessor("createdAt", {
    header: "Created At",
    sortingFn: "alphanumeric",
  }),
  accessor("createdByName", {
    header: "Created By",
    sortingFn: "alphanumeric",
  }),
  accessor("fileName", {
    header: "File Name",
    sortingFn: "alphanumeric",
  }),
  display({
    id: "progress",
    cell: (x) => {
      const { completedItems, totalItems, errorItems, cancelledItems, pendingItems } =
        x.row.original;
      return (
        <Flex direction="column" fontSize="sm" gap={0}>
          <Text>
            {completedItems}/{totalItems} completed
          </Text>
          {errorItems > 0 && <Text color="red.500">{errorItems} errored</Text>}
          {cancelledItems > 0 && <Text color="orange.500">{cancelledItems} cancelled</Text>}
          {pendingItems > 0 && <Text color="blue.500">{pendingItems} pending</Text>}
        </Flex>
      );
    },
    header: "Progress",
  }),
  display({
    id: "errors",
    cell: (x) =>
      x.row.original.errorMessage ? (
        <Text color="red.500" fontSize="sm">
          {x.row.original.errorMessage}
        </Text>
      ) : null,
    header: "Errors",
  }),
  display({
    id: "actions",
    cell: (x) => (
      <Flex gap={2}>
        <Button
          colorScheme="blue"
          size="sm"
          variant="outline"
          onClick={() => onViewItems(x.row.original.id)}
        >
          View
        </Button>
        {x.row.original.status === "running" && (
          <Button
            colorScheme="red"
            isDisabled={isStopping}
            size="sm"
            onClick={() => onStop(x.row.original.id)}
          >
            Stop
          </Button>
        )}
      </Flex>
    ),
    header: "Actions",
  }),
];
